import { useState } from "react";
import { Plus, Trash2, Lock, Users, Globe, Pencil, FileText, Share2 } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import PhotoAttach from "../PhotoAttach";

const EMPTY = { title: "", trip_date: "", airline: "", flight: "", booking_code: "", dep: "", arr: "", description: "", notes: "", privacy: "private" };
const PRIV = { private: { icon: Lock, label: "Private", tone: "text-zinc-600" }, shared: { icon: Users, label: "Shared", tone: "text-blue-600" }, public: { icon: Globe, label: "Public", tone: "text-emerald-600" } };

export default function TravelNotes({ notes, onChange }) {
  const [editing, setEditing] = useState(null); // note object or null
  const [form, setForm] = useState(EMPTY);

  const openNew = () => { setForm(EMPTY); setEditing("new"); };
  const openEdit = (n) => { setForm({ ...EMPTY, ...n }); setEditing(n.id); };

  const save = async () => {
    if (!form.title?.trim()) { toast.error("Give your trip a title"); return; }
    try {
      const body = editing === "new" ? form : { ...form, id: editing };
      await api.post("/travel/notes", body);
      toast.success("Saved");
      setEditing(null); onChange();
    } catch { toast.error("Could not save"); }
  };
  const del = async (id) => { try { await api.delete(`/travel/notes/${id}`); onChange(); } catch { toast.error("Could not delete"); } };

  const share = (n) => {
    const text = `${n.title}${n.trip_date ? ` — ${n.trip_date}` : ""}${n.flight ? `\nFlight ${n.airline || ""} ${n.flight}` : ""}${n.dep || n.arr ? `\n${n.dep || "?"} → ${n.arr || "?"}` : ""}`;
    if (navigator.share) navigator.share({ title: n.title, text }).catch(() => {});
    else { navigator.clipboard.writeText(text); toast.success("Copied trip details"); }
  };

  return (
    <div data-testid="travel-notes">
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-heading font-900 text-[#34A853] text-2xl flex items-center gap-2"><FileText className="w-6 h-6" /> My Travel Notes</h2>
        <button onClick={openNew} data-testid="note-new" className="inline-flex items-center gap-1.5 px-3 py-2 bg-primary text-white text-sm font-600 hover:bg-blue-700 transition-colors"><Plus className="w-4 h-4" /> New note</button>
      </div>

      {notes.length === 0 && !editing && <p className="text-sm text-muted-foreground">No travel notes yet. Add flights, booking codes, seats, and attach boarding passes or itineraries.</p>}

      {editing && <NoteForm form={form} setForm={setForm} onSave={save} onCancel={() => setEditing(null)} />}

      <div className="grid md:grid-cols-2 gap-4 mt-4">
        {notes.map((n) => {
          const P = PRIV[n.privacy || "private"];
          return (
            <div key={n.id} className="border border-zinc-300 bg-white p-4" data-testid={`note-${n.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="font-heading font-900 text-lg truncate">{n.title}</div>
                  {n.trip_date && <div className="text-xs font-mono text-primary mt-0.5">{n.trip_date}</div>}
                </div>
                <span className={`inline-flex items-center gap-1 text-xs ${P.tone}`}><P.icon className="w-3.5 h-3.5" />{P.label}</span>
              </div>
              {n.description && <p className="text-sm text-zinc-700 mt-2">{n.description}</p>}
              {(n.airline || n.flight || n.dep || n.arr || n.booking_code) && (
                <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs mt-3 font-mono text-zinc-600">
                  {n.flight && <div>Flight: <span className="text-zinc-900">{n.airline} {n.flight}</span></div>}
                  {n.booking_code && <div>PNR: <span className="text-zinc-900">{n.booking_code}</span></div>}
                  {(n.dep || n.arr) && <div className="col-span-2">Route: <span className="text-zinc-900">{n.dep || "?"} → {n.arr || "?"}</span></div>}
                </div>
              )}
              {n.notes && <p className="text-sm text-zinc-600 mt-2 whitespace-pre-line">{n.notes}</p>}

              <Attachments note={n} onChange={onChange} />

              <div className="flex items-center gap-3 mt-3 pt-3 border-t border-zinc-100 text-sm">
                <button onClick={() => openEdit(n)} data-testid={`note-edit-${n.id}`} className="inline-flex items-center gap-1 text-muted-foreground hover:text-primary"><Pencil className="w-4 h-4" /> Edit</button>
                <button onClick={() => share(n)} data-testid={`note-share-${n.id}`} className="inline-flex items-center gap-1 text-muted-foreground hover:text-primary"><Share2 className="w-4 h-4" /> Share</button>
                <button onClick={() => del(n.id)} data-testid={`note-delete-${n.id}`} className="inline-flex items-center gap-1 text-muted-foreground hover:text-destructive ml-auto"><Trash2 className="w-4 h-4" /> Delete</button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function NoteForm({ form, setForm, onSave, onCancel }) {
  const f = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));
  const Inp = ({ k, ph, type = "text" }) => (
    <input value={form[k] || ""} onChange={f(k)} placeholder={ph} type={type} data-testid={`note-field-${k}`}
      className="border border-zinc-300 px-3 py-2 outline-none focus:ring-2 focus:ring-primary text-sm" />
  );
  return (
    <div className="border border-primary bg-blue-50/40 p-4 mb-4" data-testid="note-form">
      <div className="grid sm:grid-cols-2 gap-2">
        <input value={form.title} onChange={f("title")} placeholder="Trip title (e.g. India Trip — December 2026)" data-testid="note-field-title"
          className="sm:col-span-2 border border-zinc-300 px-3 py-2 outline-none focus:ring-2 focus:ring-primary text-sm font-600" />
        <textarea value={form.description} onChange={f("description")} placeholder="Description (Visiting Delhi, Kochi…)" data-testid="note-field-description"
          className="sm:col-span-2 border border-zinc-300 px-3 py-2 outline-none focus:ring-2 focus:ring-primary text-sm resize-y" rows={2} />
        <Inp k="airline" ph="Airline (e.g. Air India)" />
        <Inp k="flight" ph="Flight (e.g. AI 174)" />
        <Inp k="dep" ph="From (e.g. SFO)" />
        <Inp k="arr" ph="To (e.g. DEL)" />
        <Inp k="booking_code" ph="Booking code / PNR" />
        <Inp k="trip_date" type="date" ph="Date" />
        <textarea value={form.notes} onChange={f("notes")} placeholder="Notes (Seat 14A, pickup arranged…)" data-testid="note-field-notes"
          className="sm:col-span-2 border border-zinc-300 px-3 py-2 outline-none focus:ring-2 focus:ring-primary text-sm resize-y" rows={2} />
        <select value={form.privacy} onChange={f("privacy")} data-testid="note-field-privacy" className="border border-zinc-300 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary">
          <option value="private">🔒 Private (only me)</option>
          <option value="shared">👥 Shared (people I choose)</option>
          <option value="public">🌎 Public</option>
        </select>
      </div>
      <div className="flex justify-end gap-2 mt-3">
        <button onClick={onCancel} data-testid="note-cancel" className="px-4 py-2 border border-zinc-300 text-sm">Cancel</button>
        <button onClick={onSave} data-testid="note-save" className="px-4 py-2 bg-primary text-white text-sm font-600 hover:bg-blue-700">Save note</button>
      </div>
    </div>
  );
}

function Attachments({ note, onChange }) {
  return (
    <div className="mt-3">
      <PhotoAttach
        items={note.attachments || []}
        accept="image/jpeg,image/png,image/webp,application/pdf"
        label="Attach boarding pass, ticket, itinerary…"
        testPrefix={`note-att-${note.id}`}
        onUpload={async (file) => {
          const fd = new FormData();
          fd.append("file", file);
          await api.post(`/travel/notes/${note.id}/attachments`, fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 60000 });
          onChange();
        }}
        onEdit={async (aid, patch) => { await api.put(`/travel/notes/${note.id}/attachments/${aid}`, patch); onChange(); }}
        onDelete={async (aid) => { await api.delete(`/travel/notes/${note.id}/attachments/${aid}`); onChange(); }}
      />
    </div>
  );
}
